import fr from './locales/fr.json';
import en from './locales/en.json';
import es from './locales/es.json';

export type Locale = 'fr' | 'en' | 'es';

export const locales: Locale[] = ['fr', 'en', 'es'];

export const localeNames: Record<Locale, string> = {
    fr: 'Français',
    en: 'English',
    es: 'Español',
};

export const localeFlags: Record<Locale, string> = {
    fr: '🇫🇷',
    en: '🇬🇧',
    es: '🇪🇸',
};

const messages: Record<Locale, any> = { fr, en, es };

export function getMessages(locale: Locale) {
    return messages[locale] || messages.fr;
}

/**
 * Detect locale from browser language or country.
 * Maps browser language codes to our supported locales.
 */
export function detectLocale(): Locale {
    if (typeof navigator === 'undefined') return 'fr';

    const browserLangs = navigator.languages || [navigator.language];

    for (const lang of browserLangs) {
        const code = lang.toLowerCase();
        if (code.startsWith('es')) return 'es';
        if (code.startsWith('en')) return 'en';
        if (code.startsWith('fr')) return 'fr';
    }

    return 'fr'; // default
}

/**
 * Get a nested value from an object using dot notation.
 * e.g., getNestedValue(obj, 'nav.dashboard') => obj.nav.dashboard
 */
function getNestedValue(obj: any, path: string): string | undefined {
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
        if (current === undefined || current === null) return undefined;
        current = current[key];
    }
    return typeof current === 'string' ? current : undefined;
}

/**
 * Create a translation function for a given locale.
 * Supports dot notation: t('nav.dashboard')
 * Supports interpolation: t('trial.banner', { days: 5 })
 */
export function createTranslator(locale: Locale) {
    const msgs = getMessages(locale);

    return function t(key: string, params?: Record<string, string | number>): string {
        let value = getNestedValue(msgs, key);

        // Fallback to French if key not found
        if (value === undefined) {
            value = getNestedValue(messages.fr, key);
        }

        // If still not found, return the key itself
        if (value === undefined) return key;

        // Replace interpolation params: {days} => 5
        if (params) {
            for (const [paramKey, paramValue] of Object.entries(params)) {
                value = value.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
            }
        }

        return value;
    };
}

export default messages;
